import { ContactType } from '@api/generated/graphql';
import {
  ContactMethodType,
  CreateCustomerFormValues,
} from '@components/CreateCustomer/components/CreateCustomerForm/CreateCustomerFormDefinitions';
import { EditContactsValidationType } from '@pages/agent/InboxPage/components/ContactsDrawers/validations';
import { ActionMenu, BodyText, Button } from '@servicetitan/design-system';
import { inboxLocators } from '@locators/inbox';
import { useCallback, useState } from 'react';
import { FieldArrayWithId, useFormContext } from 'react-hook-form';
import { ContactFormPrefixes, contactMethodOptionsArray } from '.';
import { ContactMethodItem } from './components/ContactMethodItem';
import strings from './strings';
import { getTestIdProps } from '@lib/testUtils';

interface ContactMethodFormProps {
  formFieldPrefix: ContactFormPrefixes;
  contactMethodFields: FieldArrayWithId<
    CreateCustomerFormValues | EditContactsValidationType,
    'customer.contacts' | 'billingCustomer.contacts',
    'id'
  >[];
  append: (contact: ContactMethodType) => void;
  remove: (index: number) => void;
  update: (index: number, contact: ContactMethodType) => void;
}

/**
 * Renders a form for entering contact method information. Can probably be reused in other forms.
 * @param formFieldPrefix - parent key for contact method form fields.
 * @param contactMethodFields
 * @param append
 * @param remove
 * @param update
 */
export function ContactMethodForm({
  formFieldPrefix,
  contactMethodFields,
  append,
  remove,
  update,
}: ContactMethodFormProps) {
  const [phoneActionsMenuVisible, setPhoneActionsMenuVisible] = useState(false);
  const [contactTypeMenuOpenByIndex, setContactTypeMenuOpenByIndex] = useState<
    Record<number, boolean>
  >({});

  const { trigger, getValues } = useFormContext<
    CreateCustomerFormValues & EditContactsValidationType
  >();

  const handleAddContactMethod = (type: ContactType) => {
    const newContactMethod: ContactMethodType = {
      type,
      value: '',
      memo: '',
      jobRemindersEnabled: false,
      invoiceStatementNotification: false,
      marketingUpdatesEnabled: false,
    };
    setPhoneActionsMenuVisible(false);
    append(newContactMethod);
  };

  const handleRemoveContactMethod = (index: number) => {
    remove(index);

    // required to trigger a dirty check when a contact is removed
    trigger(`${formFieldPrefix}.contacts`);
  };

  // toggle the type of contact method between 'phone' and 'mobile'
  const handleChangeContactMethodType = (type: ContactType, index: number) => {
    if (
      contactMethodFields[index].type !== ContactType.Phone &&
      contactMethodFields[index].type !== ContactType.MobilePhone
    ) {
      return;
    }
    const contact = getValues(`${formFieldPrefix}.contacts.${index}`);
    if (contact) {
      update(index, { ...contact, type });
    }
  };

  const toggleContactMethodTypeMenu = useCallback(
    ({ index, open }: { index: number; open: boolean }) => {
      const curr = contactTypeMenuOpenByIndex[index];
      if (curr !== open) {
        setContactTypeMenuOpenByIndex((prev) => ({ ...prev, [index]: open }));
      }
    },
    [contactTypeMenuOpenByIndex],
  );

  const isContactMethodTypeMenuOpen = useCallback(
    (index: number) => {
      return contactTypeMenuOpenByIndex[index];
    },
    [contactTypeMenuOpenByIndex],
  );

  const handleContactMethodBlur = (index: number) => {
    const contact = getValues(`${formFieldPrefix}.contacts.${index}`);
    if (contact) {
      update(index, contact);
    }
    trigger(`${formFieldPrefix}.contacts.${index}.value`);
  };

  return (
    <>
      {contactMethodFields.length > 0 &&
        contactMethodFields.map((contactMethodField, index) => (
          <ContactMethodItem
            formFieldPrefix={formFieldPrefix}
            key={`${contactMethodField.id}`}
            index={index}
            onRemove={() => handleRemoveContactMethod(index)}
            onChangeType={(type) => handleChangeContactMethodType(type, index)}
            contactMethodType={contactMethodField.type}
            onBlur={() => handleContactMethodBlur(index)}
            isContactTypeMenuOpen={isContactMethodTypeMenuOpen(index)}
            onContactTypeMenuOpenChange={(open) =>
              toggleContactMethodTypeMenu({ index, open })
            }
          />
        ))}
      <ActionMenu
        autoFlipVertically
        width='s'
        direction='br'
        open={phoneActionsMenuVisible}
        onClickOutside={() => setPhoneActionsMenuVisible(false)}
        trigger={
          <Button
            small
            fill='subtle'
            primary
            iconName='add'
            className='align-self-baseline'
            {...getTestIdProps(
              inboxLocators.customerSearch.createFormFields.addContactMethod,
            )}
            onClick={() => setPhoneActionsMenuVisible(true)}
          >
            {strings.labels.addContactMethod}
          </Button>
        }
        portal
      >
        {contactMethodOptionsArray.map((item) => (
          <ActionMenu.Item
            icon={item.icon}
            key={item.value}
            onClick={() => handleAddContactMethod(item.value)}
          >
            <BodyText>{item.label}</BodyText>
          </ActionMenu.Item>
        ))}
      </ActionMenu>
    </>
  );
}

export default ContactMethodForm;
